import { MutationTree } from 'vuex';
import { DocumentViewState, BaseDocumentSearchModel, DocumentModel, DocumentVersionModel, RelatedDocumentViewModel, DocumentPageModel, DocumentActionsModel } from '@/types';
import { DocumentViewUtils } from '@/utils/documentViewUtils';
import { getDocumentSearchModel } from './functions';

export const mutations: MutationTree<DocumentViewState> = {
  DOCUMENT_SEARCH_MODEL(state, documentSearchModel: BaseDocumentSearchModel) {
    state.DocumentSearchModel = documentSearchModel;
    state.VersionBeingViewed = {} as DocumentVersionModel;
    state.RelatedDocumentBeingViewed = {} as RelatedDocumentViewModel;
  },
  PAGE_NUMBER(state, pageNumber: number) {
    state.PageNumber = pageNumber;
  },
  PAGE_LOADING(state) {
    state.isPageLoading = true;
  },
  PAGE_DELETED(state, page: DocumentPageModel) {
    state.deletedPage = page;
  },
  DELETING(state, deleting: boolean) {
    state.isDeleting = deleting;
  },
  DOCUMENT_DELETED(state, documentId: number) {
    state.deletedDocumentId = documentId;
  },
  DOCUMENT_VERSION_BEING_VIEWED(state, version: DocumentVersionModel) {
    state.VersionBeingViewed = version;
    state.RelatedDocumentBeingViewed = {} as RelatedDocumentViewModel;
    removePageFromCache(state, { documentId: version.documentId, pageNumber: 1 } as DocumentPageModel);
  },
  RELATED_DOCUMENT_BEING_VIEWED(state, relatedDocument: RelatedDocumentViewModel) {
    state.RelatedDocumentBeingViewed = relatedDocument;
    state.VersionBeingViewed = {} as DocumentVersionModel;
    const documentId = relatedDocument?.documentSearchModel?.documentId != null ? relatedDocument.documentSearchModel.documentId : state.DocumentSearchModel.documentId;
    removePageFromCache(state, { documentId, pageNumber: 1 } as DocumentPageModel);
  },
  DOCUMENT_LOADED(state, documentModel: DocumentModel) {
    state.hasDocumentError = false;
    state.isPageLoading = false;
    state.hasDocumentLoaded = true;
    const documentSearchModel = getDocumentSearchModel(state);
    if (!documentSearchModel || documentSearchModel?.documentId === documentModel.id) {
      state.DocumentModel = documentModel;
      state.DocumentModel.conversionRequestedDate = new Date();
    }
  },
  DOCUMENT_LOAD_FAILED(state, payload: any) {
    if (payload === undefined) {
      return;
    }
    const errorMessage =
      payload && typeof payload === 'string'
        ? payload
        : payload.document.id === state.DocumentSearchModel?.documentId || payload.document.inWorkflow || payload.document.id === state.RelatedDocumentBeingViewed?.documentSearchModel?.documentId
        ? payload.errorMessage
        : '';
    if (errorMessage !== null && errorMessage !== '') {
      state.isPageLoading = false;
      state.hasDocumentError = true;
      const document: DocumentModel = {
        notification: errorMessage,
        hasNotification: true,
        errorCode: payload.document?.errorCode,
        fileExtension: payload.document?.fileExtension,
        id: payload.document?.id,
        fileName: payload.document?.fileName,
        cabinetId: payload.document?.cabinetId
      } as DocumentModel;
      state.DocumentModel = document;
    }
  },
  UNLOAD_DOCUMENT(state) {
    const initState = {
      PageNumber: 1,
      RelatedDocumentBeingViewed: {} as RelatedDocumentViewModel,
      VersionBeingViewed: {} as DocumentVersionModel,
      DocumentModel: { contentType: '', fileName: '', documentData: '', totalPages: 0 } as DocumentModel,
      DocumentSearchModel: {} as BaseDocumentSearchModel,
      hasDocumentError: false,
      isPageLoading: false,
      hasDocumentLoaded: false,
      documentPage: {} as DocumentPageModel,
      documentPageCache: [] as DocumentPageModel[],
      showLoadingSpinner: true,
      ViewConversionId: null
    } as DocumentViewState;
    Object.assign(state, initState);
  },
  PAGE_LOADED(state, documentPageModel: DocumentPageModel) {
    const documentSearchModel = getDocumentSearchModel(state);
    if (!documentSearchModel || documentSearchModel.documentId === documentPageModel.documentId) {
      state.documentPage = documentPageModel;
    }
    state.isPageLoading = false;
  },
  PAGE_DOWNLOADED(state, documentPage: DocumentPageModel) {
    const maximumPagesToCache = state.maximumNumberOfPagesToCache;
    if (maximumPagesToCache === 0) {
      return;
    }
    const cacheIndex = getPageCacheIndex(state, documentPage);
    if (cacheIndex !== -1) {
      Object.assign(state.documentPageCache[cacheIndex], documentPage);
      if (state.DocumentModel.id === documentPage.documentId && state.PageNumber === documentPage.pageNumber) {
        Object.assign(state.documentPage, documentPage);
      }
      return;
    }
    state.documentPageCache.push(documentPage);
  },
  LOAD_PAGE_FROM_CACHE(state, documentPage: DocumentPageModel) {
    const cachedPage = state.documentPageCache.find((x) => x.documentId === documentPage.documentId && x.pageNumber === documentPage.pageNumber);

    state.documentPage = { ...cachedPage } as DocumentPageModel;
  },
  REMOVE_DOC_PAGES_FROM_CACHE(state, documentId: number) {
    if (state.documentPageCache?.length) {
      const newDocumentPageCache = state.documentPageCache.filter((x) => x.documentId !== documentId);
      state.documentPageCache = [...newDocumentPageCache];
    }
  },
  UPDATE_PAGE_COUNT(state, change: number) {
    if (state.DocumentModel) {
      state.DocumentModel.totalPages += change;
    }
  },
  PAGE_LOADED_FROM_CACHE(state) {
    state.isPageLoading = false;
  },
  SHOW_LOADING_SPINNER(state, showLoadingSpinner: boolean) {
    state.showLoadingSpinner = showLoadingSpinner;
  },
  SET_CURRENT_VIEW_CONVERSION_ID(state, id: number) {
    state.CurrentViewConversionId = id;
  },
  SET_IS_CONVERSION_SLOW(state, isConversionSlow: boolean) {
    state.isConversionSlow = isConversionSlow;
  },
  SET_DOCUMENT_ACTIONS(state, { searchModel, documentActions }) {
    state.documentActionsSearch = { ...searchModel } as BaseDocumentSearchModel;
    state.documentActions = { ...documentActions } as DocumentActionsModel;
  }
};

function removePageFromCache(state: DocumentViewState, documentPage: DocumentPageModel) {
  const cacheIndex = findCacheIndex(state, documentPage);
  if (cacheIndex !== -1) {
    state.documentPageCache.splice(cacheIndex, 1);
  }
}

function getPageCacheIndex(state: DocumentViewState, documentPage: DocumentPageModel): number {
  const maximumPagesToCache = state.maximumNumberOfPagesToCache;
  let cacheIndex = findCacheIndex(state, documentPage);
  if (cacheIndex !== -1) {
    return cacheIndex;
  } else {
    if (state.documentPageCache.length >= maximumPagesToCache) {
      const pageToRemoveFromCache = getPageToRemoveFromCache(state, documentPage);
      if (pageToRemoveFromCache) {
        cacheIndex = findCacheIndex(state, pageToRemoveFromCache);
      }
      return cacheIndex;
    } else {
      return cacheIndex;
    }
  }
}

function findCacheIndex(state: DocumentViewState, documentPage: DocumentPageModel): number {
  return state.documentPageCache.findIndex((x: DocumentPageModel) => x.documentId === documentPage.documentId && x.pageNumber === documentPage.pageNumber);
}

function getPageToRemoveFromCache(state: DocumentViewState, documentPageModel: DocumentPageModel): DocumentPageModel | undefined {
  const furtherstPageFromOtherDocument = DocumentViewUtils.getFurthestPageFromOtherDocument(state.documentPageCache, documentPageModel.documentId);
  if (furtherstPageFromOtherDocument) {
    return furtherstPageFromOtherDocument;
  }

  return DocumentViewUtils.getFurthestPage(state.documentPageCache, documentPageModel.pageNumber);
}
